/**
 * Pure functions for the Survivor Game tab's round chips, the default round,
 * and every change a fan makes to their staged picks: tap, replace, move
 * priority, remove, and whether the staged set differs from what is saved.
 *
 * SUR-R19: the pick count is exactly required, so a round is saved as a full
 * set (`toggleSelection`'s full-round refusal). SUR-R22: priority is the
 * fan's single official order (`moveSelectionToPriority`). SUR-R23: picks
 * default to tap order (`toggleSelection`'s append branch).
 *
 * No React, no hooks, no clock reads, no SDK calls.
 */
import { VIEWER_CONTEST_STATUS_CALLED_OFF } from "@sportsmet/contest-engine";
import type {
  useContestList,
  useRoundList,
} from "@sportsmet/contest-sdk/private";

import type { typePickSelection } from "../pick/pickSubmitModel";
import {
  buildContestOptions,
  type typeStandingsContestOption,
} from "../poolStandings/poolStandingsModel";
import { weekdayShort } from "../scores/calendarDay";
import { survivorRoundLabel } from "./survivorGameCopy";

export type typeRoundListItem = NonNullable<
  NonNullable<ReturnType<typeof useRoundList>["data"]>["data"]
>["items"][number];
export type typeRoundGrouping = "day" | "week";
export type typeRoundChip = {
  roundId: string;
  topLabel: string;
  bottomLabel: string;
  locked: boolean;
};
export type typeSavedPick = {
  eventId: string;
  teamId: string;
  priority: number;
};
export type typeToggleResult = {
  selections: typePickSelection[];
  refusedBecauseFull: boolean;
};

export function buildRoundChips(input: {
  rounds: readonly typeRoundListItem[];
  grouping: typeRoundGrouping;
}): typeRoundChip[] {
  return input.rounds.map((round, index) => ({
    roundId: round.roundId,
    topLabel:
      input.grouping === "day"
        ? weekdayShort(round.startDate)
        : survivorRoundLabel(round.ordinal ?? index + 1),
    bottomLabel: round.label,
    locked: round.locked,
  }));
}

export function resolveSelectedRoundId(input: {
  requestedRoundId: string | undefined;
  currentRoundId: string | null | undefined;
  rounds: readonly { roundId: string }[];
}): string {
  if (input.rounds.length === 0) {
    return input.requestedRoundId ?? input.currentRoundId ?? "";
  }
  if (
    input.requestedRoundId !== undefined &&
    input.rounds.some((round) => round.roundId === input.requestedRoundId)
  ) {
    return input.requestedRoundId;
  }
  if (
    input.currentRoundId !== null &&
    input.currentRoundId !== undefined &&
    input.rounds.some((round) => round.roundId === input.currentRoundId)
  ) {
    return input.currentRoundId;
  }
  const lastRound = input.rounds.at(-1);
  // input.rounds.length === 0 already returned above, so this exists.
  return lastRound === undefined ? "" : lastRound.roundId;
}

export function selectionsFromSavedPicks(
  myPicks: readonly typeSavedPick[],
): typePickSelection[] {
  return [...myPicks]
    .sort((a, b) => a.priority - b.priority)
    .map((pick) => ({
      eventId: pick.eventId,
      teamId: pick.teamId,
      acceptsPendingSpread: false,
    }));
}

export function toggleSelection(input: {
  selections: readonly typePickSelection[];
  eventId: string;
  teamId: string;
  acceptsPendingSpread: boolean;
  requiredPickCount: number;
}): typeToggleResult {
  const existing = input.selections.find(
    (selection) => selection.eventId === input.eventId,
  );
  if (existing !== undefined) {
    if (existing.teamId === input.teamId) {
      return {
        selections: input.selections.filter(
          (selection) => selection.eventId !== input.eventId,
        ),
        refusedBecauseFull: false,
      };
    }
    const replaced = input.selections.map((selection) =>
      selection.eventId === input.eventId
        ? {
            eventId: input.eventId,
            teamId: input.teamId,
            acceptsPendingSpread: input.acceptsPendingSpread,
          }
        : selection,
    );
    return { selections: replaced, refusedBecauseFull: false };
  }
  if (input.selections.length < input.requiredPickCount) {
    return {
      selections: [
        ...input.selections,
        {
          eventId: input.eventId,
          teamId: input.teamId,
          acceptsPendingSpread: input.acceptsPendingSpread,
        },
      ],
      refusedBecauseFull: false,
    };
  }
  if (input.requiredPickCount <= 1) {
    return {
      selections: [
        {
          eventId: input.eventId,
          teamId: input.teamId,
          acceptsPendingSpread: input.acceptsPendingSpread,
        },
      ],
      refusedBecauseFull: false,
    };
  }
  return { selections: [...input.selections], refusedBecauseFull: true };
}

export function moveSelectionToPriority(input: {
  selections: readonly typePickSelection[];
  eventId: string;
  priority: number;
}): typePickSelection[] {
  const moved = input.selections.find(
    (selection) => selection.eventId === input.eventId,
  );
  if (moved === undefined) {
    return [...input.selections];
  }
  const rest = input.selections.filter(
    (selection) => selection.eventId !== input.eventId,
  );
  const toIndex = Math.min(Math.max(input.priority - 1, 0), rest.length);
  return [...rest.slice(0, toIndex), moved, ...rest.slice(toIndex)];
}

export function removeSelection(input: {
  selections: readonly typePickSelection[];
  eventId: string;
}): typePickSelection[] {
  return input.selections.filter(
    (selection) => selection.eventId !== input.eventId,
  );
}

export function isSelectionChanged(input: {
  selections: readonly typePickSelection[];
  myPicks: readonly typeSavedPick[];
}): boolean {
  const selectionKeys = input.selections.map(
    (selection) => `${selection.eventId}|${selection.teamId}`,
  );
  const savedKeys = [...input.myPicks]
    .sort((a, b) => a.priority - b.priority)
    .map((pick) => `${pick.eventId}|${pick.teamId}`);
  if (selectionKeys.length !== savedKeys.length) return true;
  return selectionKeys.some((key, index) => key !== savedKeys[index]);
}

type typeGameContestListItem = NonNullable<
  NonNullable<ReturnType<typeof useContestList>["data"]>["data"]
>["items"][number];

/**
 * ISSUE-1331. A contest the Game tab can look back at: any contest of the
 * season that has started and was not called off, other than the viewer's
 * own. An upcoming contest has no board to look back at, and the viewer's
 * own contest is the playable Game tab, not a look back.
 */
function isLookBackContest(
  item: typeGameContestListItem,
  viewerContestId: string | null,
): boolean {
  return (
    item.contestId !== viewerContestId &&
    item.status !== "upcoming" &&
    item.status !== VIEWER_CONTEST_STATUS_CALLED_OFF
  );
}

/**
 * The Game tab's contest menu: the viewer's contest plus every contest they
 * can look back at, newest first, labeled like the Results tab's menu. Empty
 * when there is nothing to look back at, so the menu stays hidden for a
 * season with one contest.
 */
export function buildGameContestOptions(input: {
  items: readonly typeGameContestListItem[];
  viewerContestId: string | null;
}): typeStandingsContestOption[] {
  const lookBack = input.items.filter((item) =>
    isLookBackContest(item, input.viewerContestId),
  );
  if (lookBack.length === 0) return [];
  return buildContestOptions(
    input.items.filter(
      (item) =>
        item.contestId === input.viewerContestId ||
        isLookBackContest(item, input.viewerContestId),
    ),
  );
}

/**
 * The contest `?contestId=` asks the Game tab to look back at, or null when
 * the tab shows the viewer's own contest. A link to the viewer's contest, to
 * an upcoming or called-off contest, or to a contest not in this season
 * falls back to the viewer's contest rather than a dead board.
 */
export function resolveLookBackContest(input: {
  requestedContestId: string | undefined;
  viewerContestId: string | null;
  items: readonly typeGameContestListItem[];
}): { contestId: string; name: string } | null {
  const requested = input.requestedContestId;
  if (requested === undefined || requested === "") return null;
  const item = input.items.find(
    (candidate) => candidate.contestId === requested,
  );
  if (item === undefined || !isLookBackContest(item, input.viewerContestId)) {
    return null;
  }
  return { contestId: item.contestId, name: item.name };
}
